import "./style.scss";

import FormatLineSpacingOutlinedIcon from "@mui/icons-material/FormatLineSpacingOutlined";
import RepeatOneOutlinedIcon from "@mui/icons-material/RepeatOneOutlined";
import { Collapse, Tooltip } from "@mui/material";
import type { INewStatementForm } from "components/PartnerStatementsSettings/types";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface ISheetLine {
    /**
     * Number of the line
     */
    lineNumber: number;
    /**
     * Found crealo var in sheet
     */
    crealoVariables: string[];
    /**
     * Range of the line correspond to google sheet line range
     */
    lineRange: string;
    /**
     * Keys for all crealo variables
     */
    variablesKeys: string[];
    /**
     * Full name for variables
     */
    variablesNames: Record<string, string>;
}

const SheetDataByLine = ({ lineNumber, crealoVariables, lineRange, variablesKeys, variablesNames }: ISheetLine) => {
    const [t] = useTranslation();
    const {
        setValue,
        watch,
        control,
        clearErrors,
        formState: { errors },
    } = useFormContext<INewStatementForm>();
    const iterationRange = watch("iterationRange");
    const isSelectedIteration = iterationRange === lineRange;

    const onSelectIterationRange = () => {
        setValue("iterationRange", lineRange, { shouldDirty: true });
        clearErrors("iterationRange");
    };

    const renderVariablesOptions = (props: any, option: any, data: any) => {
        return (
            <li {...props} key={`variable-opt-${option}`} className={`new-integration-options-list`}>
                {variablesNames[option]}
            </li>
        );
    };

    return (
        <div className="sheet-line-section">
            <div className="sheet-line-head">
                <div className="sheet-line-title-wrapper">
                    <span className="sheet-line-title">
                        {t("PartnerStatements.Line")} {lineNumber}
                    </span>
                    <span className="sheet-line-range">{lineRange}</span>
                </div>
                <Tooltip title={!isSelectedIteration && t("PartnerStatements.ChooseRepetitive")} placement="top" arrow>
                    <div className="sheet-line-repeat" onClick={onSelectIterationRange}>
                        {isSelectedIteration && (
                            <span className="iterable-line">{t("PartnerStatements.IterativeLine")}</span>
                        )}
                        {!iterationRange && (
                            <span className="choose-iterable-line">{t("PartnerStatements.ChooseIterativeLine")}</span>
                        )}
                        <RepeatOneOutlinedIcon
                            className={`repeat-icon ${isSelectedIteration ? "selected" : "not-selected"}`}
                        />
                    </div>
                </Tooltip>
            </div>

            <div className="sheet-line-variables-wrapper">
                <Collapse in={isSelectedIteration} timeout="auto">
                    <div className="sheet-line-repeat-info">
                        <FormatLineSpacingOutlinedIcon sx={{ height: "18px", width: "18px" }} />
                        {t("PartnerStatements.VariablesWillBeRepeat")}
                    </div>
                </Collapse>
                {crealoVariables.map((crealoVar) => {
                    return (
                        <div className="sheet-crealo-variable-matching-wrapper" key={crealoVar}>
                            <Tooltip title={crealoVar} placement="top" arrow>
                                <div className="sheet-crealo-variable">{crealoVar}</div>
                            </Tooltip>
                            <div className="crealo-var-correspondence">
                                <AutocompleteFormComponent
                                    controlName={`variables.${crealoVar}`}
                                    control={control}
                                    required
                                    defaultFilterOptions
                                    renderOption={renderVariablesOptions}
                                    options={variablesKeys}
                                    renderOptionText={(option) => variablesNames[option]}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    placeholder={t("PartnerStatements.Match")}
                                    error={errors.variables && errors?.variables[crealoVar]}
                                    size="small"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SheetDataByLine;
